import { useMutation } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, NavLink, Navigate, Outlet, useLocation, useNavigate } from 'react-router';

import { api, unwrap } from '../api/client.js';
import { errorMessage } from '../errors.js';
import { toLanguage } from '../i18n.js';
import { useMe, useSignOut, type Me } from '../session.js';
import { useTrialDays } from '../trial.js';
import { LanguageSwitch, useLanguage } from './LanguageSwitch.js';
import { ThemeToggle } from './ThemeToggle.js';
import { Button, LinkButton, LoadError, Loading, buttonClass } from './ui.js';

function SkipLink() {
  const { t } = useTranslation();
  return (
    <a
      href="#main"
      className="sr-only focus:not-sr-only focus:absolute focus:left-2 focus:top-2 focus:z-20 focus:rounded focus:bg-surface focus:px-3 focus:py-2"
    >
      {t('app.skipToContent')}
    </a>
  );
}

/** The mark «Puls» and the name (sheet: .logo). */
export function LogoMark() {
  return (
    <svg viewBox="0 0 48 48" aria-hidden="true">
      <path d="M24 3l17 5.5v13.5c0 10-7 17.5-17 22.5C14 39.5 7 32 7 22V8.5z" fill="currentColor" />
      <path
        d="M13 25h6l3-7 4 12 3-6h6"
        fill="none"
        stroke="var(--bg)"
        strokeWidth="3.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** The logo leads to the overview when signed in, else back to the website (or the sign-in). */
function Logo({ signedIn }: { signedIn: boolean }) {
  const { t } = useTranslation();
  const name = t('app.name');
  const site = signedIn ? null : landingUrl();
  if (site)
    return (
      <a className="logo" href={site} aria-label={t('nav.toWebsite')}>
        <LogoMark />
        {name}
      </a>
    );
  return (
    <Link className="logo" to="/" aria-label={name}>
      <LogoMark />
      {name}
    </Link>
  );
}

function CheckMark() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path d="M5 12l4 4L19 6" />
    </svg>
  );
}

/** The sample grade beside the forms: a ring at 84 of 100 (design/pages/app/auth.html). */
function SampleRing() {
  const circumference = 2 * Math.PI * 42;
  return (
    <svg viewBox="0 0 100 100" aria-hidden="true">
      <circle cx="50" cy="50" r="42" fill="none" stroke="var(--line-strong)" strokeWidth="7" />
      <circle
        cx="50"
        cy="50"
        r="42"
        fill="none"
        stroke="var(--g-b)"
        strokeWidth="7"
        strokeDasharray={`${(circumference * 84) / 100} ${circumference}`}
        transform="rotate(-90 50 50)"
      />
      <text
        x="50"
        y="61"
        textAnchor="middle"
        fontSize="34"
        fontWeight="800"
        fill="currentColor"
        fontFamily="Manrope, sans-serif"
      >
        B
      </text>
    </svg>
  );
}

/** The brand side of the sign-in pages: what the product does, the trial and a sample grade. */
function BrandSide({ signedIn }: { signedIn: boolean }) {
  const { t } = useTranslation();
  const trialDays = useTrialDays();
  const points = [t('public.point1'), t('public.point2')];
  if (trialDays !== undefined) points.push(t('public.point3', { count: trialDays }));
  return (
    <aside className="brand">
      <div className="top">
        <Logo signedIn={signedIn} />
        <p className="tag">{t('public.tagline')}</p>
        <ul>
          {points.map((point) => (
            <li key={point}>
              <CheckMark />
              <span>{point}</span>
            </li>
          ))}
        </ul>
      </div>
      <div className="example" aria-hidden="true">
        <span className="lab">{t('public.sample')}</span>
        <div className="ring">
          <SampleRing />
          <div>
            <div className="num">
              84<small>/ 100</small>
            </div>
            <div className="cap">{t('public.sampleText')}</div>
          </div>
        </div>
      </div>
    </aside>
  );
}

/**
 * The public website the app belongs to: SITE_URL of the build, else the app host without "app."
 * (app.guard.findyour.dance → guard.findyour.dance). Null where there is none (local development).
 */
export function landingUrl(location: Pick<Location, 'protocol' | 'hostname'> = window.location) {
  if (__SITE_URL__) return `${__SITE_URL__}/`;
  if (!location.hostname.startsWith('app.')) return null;
  return `${location.protocol}//${location.hostname.slice(4)}/`;
}

/**
 * Sign-in, registration and link pages (design/pages/app/auth.html, SP-UX10.2a): the brand side
 * beside the form card, languages and theme above it; on phones the brand shrinks to a band.
 * `wide`: the public pages under the header of the site (Quick-Check, quick-check.html, SP-UX10.2b):
 * guests get the guest header and the website's footer, signed-in people the app's header. The
 * fixes page /fix/:token has a frame of its own, in the owner's language (SharedFixes).
 */
export function PublicLayout({ wide = false }: { wide?: boolean }) {
  const me = useMe();
  if (wide)
    return (
      <div className="flex min-h-screen flex-col">
        <SkipLink />
        {me.data ? <AppHeader me={me.data} /> : <GuestHeader />}
        <main id="main" className="container w-full flex-1">
          <Outlet />
        </main>
        {me.data ? null : <WebsiteFooter />}
      </div>
    );
  return (
    <div className="auth">
      <SkipLink />
      <BrandSide signedIn={Boolean(me.data)} />
      <main id="main" className="form-side">
        <div className="topbar">
          <LanguageSwitch signedIn={Boolean(me.data)} />
          <ThemeToggle />
        </div>
        <div className="auth-card">
          <Outlet />
        </div>
      </main>
    </div>
  );
}

function VerifyBanner() {
  const { t } = useTranslation();
  const resend = useMutation({ mutationFn: () => unwrap(api.POST('/auth/resend-verification')) });
  return (
    <div className="banner warn">
      <span>{t('banner.verify')}</span>
      {resend.isSuccess ? (
        <span role="status" className="font-semibold">
          {t('banner.sent')}
        </span>
      ) : (
        <Button
          variant="secondary"
          size="sm"
          busy={resend.isPending}
          onClick={() => resend.mutate()}
        >
          {t('banner.resend')}
        </Button>
      )}
      {resend.isError ? (
        <span role="alert" className="basis-full">
          {errorMessage(resend.error, t)}
        </span>
      ) : null}
    </div>
  );
}

/**
 * "Business kostenlos testen: noch 13 Tage" (v2 §14): while the trial runs; a paid plan ends
 * the trial (checkout clears trialEndsAt), and so does the worker on the last day. The last three
 * days stand out.
 */
export function TrialBanner({
  trialEndsAt,
  now = new Date(),
}: {
  trialEndsAt: string | null;
  now?: Date;
}) {
  const { t } = useTranslation();
  if (!trialEndsAt) return null;
  const left = Math.ceil((Date.parse(trialEndsAt) - now.getTime()) / 86_400_000);
  if (left <= 0) return null;
  return (
    <div className={`banner print:hidden${left <= 3 ? ' warn' : ''}`}>
      <span>{t('banner.trial', { count: left })}</span>
      <LinkButton to="/billing" variant="primary" size="sm">
        {t('banner.trialCta')}
      </LinkButton>
    </div>
  );
}

/** Initials of the account for the chip in the header ("Praxis Sommer" → "PS"). */
function initials(name: string) {
  const words = name.split(/[\s.@_-]+/).filter(Boolean);
  return (
    words
      .slice(0, 2)
      .map((word) => word.charAt(0))
      .join('')
      .toUpperCase() || '·'
  );
}

/**
 * The menu of the header on phones (design/foundation/behavior.md): its button opens it; a link in
 * it, a new page or Escape close it, Escape gives the focus back to the button.
 */
function useHeaderMenu() {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const button = useRef<HTMLButtonElement>(null);
  useEffect(() => setOpen(false), [location.pathname]);
  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      setOpen(false);
      button.current?.focus();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);
  return { open, toggle: () => setOpen((value) => !value), close: () => setOpen(false), button };
}

function MenuButton({ menu }: { menu: ReturnType<typeof useHeaderMenu> }) {
  const { t } = useTranslation();
  return (
    <button
      ref={menu.button}
      type="button"
      className="theme-toggle menu-btn"
      aria-expanded={menu.open}
      aria-controls="menu"
      aria-label={t('nav.menu')}
      onClick={menu.toggle}
    >
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        aria-hidden="true"
      >
        <path d="M4 7h16M4 12h16M4 17h16" />
      </svg>
    </button>
  );
}

/** Header of the signed-in area (design/pages/app/overview.html): links, languages, theme, account. */
function AppHeader({ me }: { me: Me }) {
  const { t } = useTranslation();
  const signOut = useSignOut();
  const navigate = useNavigate();
  const menu = useHeaderMenu();

  const links = [
    { to: '/', label: t('nav.overview'), end: true },
    { to: '/requests', label: t('nav.requests') },
    { to: '/notifications', label: t('nav.notifications') },
    { to: '/billing', label: t('nav.billing') },
    { to: '/account', label: t('nav.account') },
    ...(me.user.isOperator ? [{ to: '/admin', label: t('nav.admin') }] : []),
  ];
  // The site pages belong to the overview, as in the mock-ups (site-dashboard.html).
  const inSites = useLocation().pathname.startsWith('/sites/');
  const navLinks = (onClick?: () => void) =>
    links.map((link) =>
      link.to === '/' && inSites ? (
        <Link key={link.to} to={link.to} aria-current="page" onClick={onClick}>
          {link.label}
        </Link>
      ) : (
        <NavLink key={link.to} to={link.to} end={link.end ?? false} onClick={onClick}>
          {link.label}
        </NavLink>
      ),
    );
  const logout = (
    <button
      type="button"
      className={buttonClass('ghost', 'sm')}
      onClick={() =>
        void signOut().then(() => {
          void navigate('/login');
        })
      }
    >
      {t('nav.logout')}
    </button>
  );
  const name = me.account.name || me.user.email;
  return (
    <header className="app-header print:hidden">
      <div className="container">
        <nav className="nav" aria-label={t('nav.label')}>
          <Logo signedIn />
          <div className="links">{navLinks()}</div>
          <div className="ctl">
            <LanguageSwitch signedIn />
            <ThemeToggle />
            <span className="user" title={name}>
              <i aria-hidden="true">{initials(name)}</i>
              <span>{name}</span>
            </span>
            {logout}
            <MenuButton menu={menu} />
          </div>
        </nav>
        <div className="menu" id="menu" hidden={!menu.open}>
          {navLinks(menu.close)}
          <div className="ctl">
            <LanguageSwitch signedIn />
            {logout}
          </div>
        </div>
      </div>
    </header>
  );
}

/** Signed-in area: header, banners, content in the container of the mock-ups. */
export function AppLayout() {
  const { t } = useTranslation();
  const me = useMe();
  const location = useLocation();

  if (me.isPending) return <Loading />;
  if (me.isError)
    return <LoadError message={errorMessage(me.error, t)} onRetry={() => void me.refetch()} />;
  if (!me.data)
    return (
      <Navigate
        to={`/login?next=${encodeURIComponent(location.pathname + location.search)}`}
        replace
      />
    );

  return (
    <div className="flex min-h-screen flex-col">
      <SkipLink />
      <AppHeader me={me.data} />
      {/* On paper the page margins are the sheet's alone (@page, report-print.html). */}
      <main id="main" className="container w-full flex-1 print:max-w-none print:px-0">
        <div className="banners">
          {me.data.user.emailVerified ? null : <VerifyBanner />}
          <TrialBanner trialEndsAt={me.data.account.trialEndsAt} />
        </div>
        <Outlet />
      </main>
    </div>
  );
}

type Lang = 'de' | 'en' | 'uk';
const LANGS: readonly Lang[] = ['de', 'en', 'uk'];
const LANGUAGE_NAMES: Record<Lang, string> = { de: 'Deutsch', en: 'English', uk: 'Українська' };

/** Pages of the website in the visitor's language (apps/landing/src/layouts/Base.astro). */
const WEBSITE: Record<Lang, { home: string; features: string; pricing: string; bot: string }> = {
  de: { home: '/', features: '/#leistungen', pricing: '/preise', bot: '/bot' },
  en: { home: '/en', features: '/en#features', pricing: '/en/pricing', bot: '/en/bot' },
  uk: { home: '/uk', features: '/uk#features', pricing: '/uk/pricing', bot: '/uk/bot' },
};
const LEGAL = ['impressum', 'datenschutz', 'agb', 'avv', 'subprozessoren'] as const;
const LEGAL_NAMES: Record<(typeof LEGAL)[number], string> = {
  impressum: 'Impressum',
  datenschutz: 'Datenschutz',
  agb: 'AGB',
  avv: 'AVV',
  subprozessoren: 'Subprozessoren',
};

/** The website's links for the UI language, or null where there is no website (development). */
function useWebsite() {
  const { i18n } = useTranslation();
  const site = landingUrl();
  if (!site) return null;
  const base = site.replace(/\/$/, '');
  const lang = toLanguage(i18n.language);
  const pages = WEBSITE[lang];
  return {
    lang,
    href: (path: string) => `${base}${path}`,
    features: `${base}${pages.features}`,
    pricing: `${base}${pages.pricing}`,
    guide: `${base}/ratgeber`,
    bot: `${base}${pages.bot}`,
  };
}

/**
 * Header for guests on public pages (404, Quick-Check; design/pages/landing/404.html and
 * quick-check.html): logo, the website's sections, languages, theme, sign-in and the free check;
 * on phones the sections, languages, sign-in and check sit in the menu.
 */
function GuestHeader() {
  const { t } = useTranslation();
  const menu = useHeaderMenu();
  const website = useWebsite();
  const onCheck = useLocation().pathname.startsWith('/check');
  const sections = (onClick?: () => void) =>
    website ? (
      <>
        <a href={website.features} onClick={onClick}>
          {t('website.features')}
        </a>
        <a href={website.pricing} onClick={onClick}>
          {t('website.pricing')}
        </a>
        <a href={website.guide} hrefLang="de" onClick={onClick}>
          {t('website.guide')}
        </a>
      </>
    ) : null;
  const actions = (onClick?: () => void) => (
    <>
      <LinkButton to="/login" variant="secondary" size="sm" onClick={onClick}>
        {t('notFound.login')}
      </LinkButton>
      {onCheck ? null : (
        <LinkButton to="/check" variant="primary" size="sm" onClick={onClick}>
          {t('quickCheck.submit')}
        </LinkButton>
      )}
    </>
  );
  return (
    <header className="app-header">
      <div className="container">
        <nav className="nav" aria-label={t('nav.label')}>
          <Logo signedIn={false} />
          {website ? <div className="links">{sections()}</div> : null}
          <div className="ctl">
            <LanguageSwitch signedIn={false} />
            <ThemeToggle />
            {actions()}
            <MenuButton menu={menu} />
          </div>
        </nav>
        <div className="menu" id="menu" hidden={!menu.open}>
          {sections(menu.close)}
          <div className="ctl">
            <LanguageSwitch signedIn={false} />
            {actions(menu.close)}
          </div>
        </div>
      </div>
    </header>
  );
}

/**
 * The footer of the website for guests on public pages (quick-check.html, landing.css .footer):
 * product, the legal pages (German, binding) and the languages; only where there is a website to
 * link to. The languages change the page in place, as the switch in the header does: a link to the
 * website would leave a Quick-Check result behind.
 */
function WebsiteFooter() {
  const { t } = useTranslation();
  const website = useWebsite();
  const language = useLanguage(false);
  if (!website) return null;
  return (
    <footer className="container">
      <div className="footer">
        <div>
          <a className="logo" href={website.href(WEBSITE[website.lang].home)}>
            <LogoMark />
            {t('app.name')}
          </a>
        </div>
        <nav aria-labelledby="footer-product">
          <h2 id="footer-product">{t('website.product')}</h2>
          <ul>
            <li>
              <a href={website.features}>{t('website.features')}</a>
            </li>
            <li>
              <a href={website.pricing}>{t('website.pricing')}</a>
            </li>
            <li>
              <a href={website.guide} hrefLang="de">
                {t('website.guide')}
              </a>
            </li>
            <li>
              <a href={website.bot}>{__BOT_NAME__}</a>
            </li>
          </ul>
        </nav>
        <nav aria-labelledby="footer-legal">
          <h2 id="footer-legal">{t('website.legal')}</h2>
          <ul>
            {LEGAL.map((page) => (
              <li key={page}>
                <a href={website.href(`/${page}`)} hrefLang="de" lang="de">
                  {LEGAL_NAMES[page]}
                </a>
              </li>
            ))}
          </ul>
        </nav>
        <div role="group" aria-labelledby="footer-language">
          <h2 id="footer-language">{t('website.language')}</h2>
          <ul>
            {LANGS.map((code) => (
              <li key={code}>
                <button
                  type="button"
                  lang={code}
                  aria-pressed={code === language.current}
                  onClick={() => void language.change(code)}
                >
                  {LANGUAGE_NAMES[code]}
                </button>
              </li>
            ))}
          </ul>
        </div>
        <div className="legal">
          <span>{t('website.noAdvice')}</span>
          {/* As on the website: the Ukrainian pages say that the German legal text binds. */}
          {website.lang === 'uk' ? <span>{t('website.legalNote')}</span> : null}
          <span>
            © {new Date().getFullYear()} {t('app.name')}
          </span>
        </div>
      </div>
    </footer>
  );
}

/**
 * 404 (design/pages/landing/404.html): guests go to the website, the free check or the sign-in;
 * signed-in people keep the header of the app and go back to their overview (audit NF-1).
 */
export function NotFound() {
  const { t } = useTranslation();
  const me = useMe();
  if (me.isPending) return <Loading />;
  const signedIn = Boolean(me.data);
  const site = landingUrl();
  return (
    <div className="flex min-h-screen flex-col">
      <SkipLink />
      {me.data ? <AppHeader me={me.data} /> : <GuestHeader />}
      <main id="main" className="container w-full flex-1">
        <div className="nf">
          <span className="code">404</span>
          <h1>{t('notFound.title')}</h1>
          <p>{t('notFound.text')}</p>
          <div className="row">
            {signedIn ? (
              <LinkButton to="/" variant="primary">
                {t('notFound.overview')}
              </LinkButton>
            ) : (
              <>
                {site ? (
                  <a className={buttonClass('primary')} href={site}>
                    {t('notFound.home')}
                  </a>
                ) : null}
                <LinkButton to="/check" variant={site ? 'secondary' : 'primary'}>
                  {t('notFound.check')}
                </LinkButton>
              </>
            )}
          </div>
        </div>
      </main>
      {signedIn ? null : <WebsiteFooter />}
    </div>
  );
}
